import * as React from 'react';
import {
    Question,
    RichTextContent,
    TrainedModelConfig,
    TrainedModelSummary
} from '@nexxt/common/types';
import * as uuid from 'uuid';
import Select from 'react-select';
import TrainedModelEntry from './TrainedModelEntry';

interface TrainedModelEditorProps {
    lang: { value: string; label: string };
    question: Question;
    trainedModelSummaries: TrainedModelSummary[];
    handleTrainedModelChange;
    editEnabled: boolean;
}

const TrainedModelEditor = ({
    lang,
    question,
    trainedModelSummaries,
    handleTrainedModelChange,
    editEnabled
}: TrainedModelEditorProps): JSX.Element => {
    const langId = lang.value;
    const baseQuestionText = question.questionTexts
        .find((item) => item.language == langId)
        .contents.reduce((prev, curr, i) => {
            if ((curr as RichTextContent).richText) {
                return (prev +=
                    curr &&
                    (curr as RichTextContent).richText.blocks[0].text +
                        (i > 0 ? '; ' : ''));
            } else {
                return prev;
            }
        }, '');
    const BASE_TRAINING_EXAMPLES = [
        {
            prime_question: baseQuestionText,
            prime_response: '',
            probe_question: ''
        },
        {
            prime_question: baseQuestionText,
            prime_response: '',
            probe_question: ''
        },
        {
            prime_question: baseQuestionText,
            prime_response: '',
            probe_question: ''
        }
    ];
    const trainedModelMapping =
        question.conversationalSettings.smartProbeSettings.trainedModelMapping;
    const trainedModel = trainedModelMapping?.[langId] as TrainedModelConfig;
    const customTrainedModelExample = (
        trainedModelMapping?.[langId] as TrainedModelConfig
    )?.training_examples
        ? {
              ...trainedModel,
              label: 'Custom trained model'
          }
        : {
              label: 'Custom trained model',
              id: uuid.v4(),
              training_examples: BASE_TRAINING_EXAMPLES
          };
    const trainedModelSelectOptionsForLang = [
        {
            label: 'Default inca trained model',
            id: null,
            training_examples: null
        },
        ...trainedModelSummaries
            .filter((summary) => summary.trainedModelLangId == langId)
            .map((summary) => ({
                label: summary.trainedModelLabel,
                id: summary.trainedModelId.toString(),
                training_examples: null
            })),
        customTrainedModelExample
    ];
    const langLabel = lang.label;
    // Checks for if there is a Pretrained Model selected
    // Or if a Trained Model is selected
    // Or return default inca trained model
    const selectedTrainedModel =
        trainedModelSelectOptionsForLang.find(
            (opt) =>
                opt.id == trainedModelMapping?.[langId] ||
                (trainedModel?.id && opt.id == trainedModel?.id)
        ) || trainedModelSelectOptionsForLang[0];

    return (
        <div className="mb-4">
            <div className="font-semibold mb-2">
                {`${langLabel} Trained Model`}
            </div>
            <Select
                options={trainedModelSelectOptionsForLang}
                value={selectedTrainedModel}
                onChange={(e) => {
                    if (e.training_examples) {
                        handleTrainedModelChange(
                            {
                                id: e.id,
                                training_examples: e.training_examples
                            },
                            langId
                        );
                    } else {
                        handleTrainedModelChange(e.id, langId);
                    }
                }}
            />
            {trainedModel?.training_examples && (
                <TrainedModelEntry
                    langId={langId}
                    label={`${langLabel} Trained Model Examples`}
                    disabled={!editEnabled}
                    trainedModel={trainedModel}
                    onTrainedModelChange={handleTrainedModelChange}
                    baseQuestion={baseQuestionText}
                />
            )}
        </div>
    );
};

export default TrainedModelEditor;
